import React, { useState, useEffect, useContext, useRef, useImperativeHandle } from 'react';
import { Box, Typography } from '@mui/material';

import { LayoutContext } from 'contexts/LayoutContext/LayoutContext';
import cvtLogo from 'shared/img/logos/cvt.svg';

type Milestone = {
  t: number;
  value: number;
  label: string;
  labelPos?: 'above' | 'below';
  emoji?: string;
  image?: string;
  pathLabelPos?: 'above' | 'below';
  pathLabelAlign?: 'start' | 'middle' | 'end';
  hidePathLabel?: boolean;
};

const vibeMilestones: Milestone[] = [
  { t: 0, value: 20, label: 'Vibe Coding\non your own', emoji: '🚀', labelPos: 'above', pathLabelAlign: 'end', pathLabelPos: 'above' },
  { t: 1, value: 50, label: 'Mind = Blown\nThis feels amazing', labelPos: 'above', emoji: '🤯', pathLabelPos: 'above', pathLabelAlign: 'end' },
  { t: 2, value: 80, label: 'Great, I have an amazing product\nsurely this will go viral!', labelPos: 'above', emoji: '🤩', pathLabelPos: 'below', pathLabelAlign: 'end' },
  { t: 3, value: 30, label: 'Why is nobody using my \namazing solution?', labelPos: 'below', emoji: '😡' },
  { t: 4, value: 47, label: '6 sleepless nights later\nthis time they\'ll love it... right?', labelPos: 'above', emoji: '🤩', pathLabelAlign: 'start' },
  { t: 5, value: 35, label: 'Something broke \nand I can\'t figure out why', labelPos: 'above', emoji: '🔥' },
  { t: 6, value: 60, label: 'Rebuilt from scratch\nThis time will be different!', labelPos: 'above', emoji: '🤩' },
  { t: 7, value: 50, label: 'Wait, what was I\neven building again?', labelPos: 'above', emoji: '😵‍💫', pathLabelPos: 'above', pathLabelAlign: 'end' },
  { t: 8, value: 58, label: 'I remember now, but\neverything\'s broken again', labelPos: 'below', emoji: '🤦', pathLabelPos: 'above', pathLabelAlign: 'end' },
  { t: 9, value: 54, label: 'I\'m stuck\nMy AI is gaslighting me\nNo one to turn to', labelPos: 'below', emoji: '🙃' },
  { t: 10, value: 0, label: 'I give up!\nthis is too hard', labelPos: 'above', emoji: '💀' },
];

const cvtMilestones: Milestone[] = [
  { t: 0, value: 35, label: 'Value Coding\nwith CVT', image: cvtLogo, labelPos: 'above', pathLabelAlign: 'end' },
  { t: 1, value: 25, label: "Let's first talk to users\nno coding", labelPos: 'below', emoji: '🎯', pathLabelAlign: 'middle' },
  { t: 2, value: 60, label: 'First release is live\n let\'s talk to users again', labelPos: 'above', emoji: '😊' },
  { t: 3, value: 50, label: 'Honest user feedback\nAdjusting course', labelPos: 'above', emoji: '🔄' },
  { t: 4, value: 70, label: 'Iterated based on data\nnot assumptions', labelPos: 'above', emoji: '📊' },
  { t: 5, value: 68, label: 'Go-to-market strategy is set\nwe know who we\'re selling to', labelPos: 'above', emoji: '📣' },
  { t: 6, value: 78, label: 'Customers are adopting\nand they\'re actually paying', labelPos: 'above', emoji: '🤑' },
  { t: 7, value: 90, label: 'Retention is growing\nchurn is under control', labelPos: 'below', emoji: '🤩' },
  { t: 8, value: 100, label: 'Outcome Achieved!', labelPos: 'below', emoji: '🏆' },
];

const inhouseMilestones: Milestone[] = [
  { t: 0, value: 10, label: "Hire your\nown devs", emoji: "👨‍💻", labelPos: "below" },
  { t: 1, value: 10, label: "Posted the job listings\nhopefully someone good applies", labelPos: "below", emoji: "📝" },
  { t: 2, value: 20, label: "Interviewed many candidates\nfinally got our first hire", labelPos: "above", emoji: "🤝" },
  { t: 3, value: 10, label: "Our first hire didn't work out\nback to square one", labelPos: "below", emoji: "😩" },
  { t: 4, value: 20, label: "New team onboarding\nthey don't know the codebase yet", labelPos: "below", emoji: "📚" },
  { t: 5, value: 25, label: "Trial and error\nlots of rework", labelPos: "below", emoji: "🔨" },
  { t: 6, value: 30, label: "OK the team is clicking now\nthings are starting to move", labelPos: "below", emoji: "⚡" },
  { t: 7, value: 40, label: "Gaining real momentum\nshipping consistently", labelPos: "below", emoji: "🚢", pathLabelPos: 'above', pathLabelAlign: 'middle' },
  { t: 8, value: 73, label: "Pushing hard to finish\nbut it's been a long road", labelPos: "above", emoji: "😓" },
  { t: 9, value: 91, label: "Almost there\njust a few more sprints", labelPos: "above", emoji: "🏃" },
  { t: 10, value: 100, label: "Outcome achieved\n(late, over budget)", labelPos: "below", emoji: "🏁" },
];

// "Keep at it" dashed continuation for vibe coding
// Dashed branch from penultimate vibe step (t=9) — "the 1% path"
const vibeKeepAtIt: Milestone[] = [
  { t: 9, value: 54, label: '' },
  { t: 10, value: 80, label: 'Keep at it,\n1% chance of\neventually succeeding', labelPos: 'below', emoji: '🎰' },
];

// ============ CONFIG — tweak these ============
const LINE_DRAW_MS = 1000;    // how long the line takes to draw to next point
const SCROLL_DURATION = 1000;  // ms for scroll animation to reach next point
const CHART_FIXED_W = 2500;   // total SVG width (scrollable)
const CHART_HEIGHT_VH = 0.80;  // chart height as fraction of viewport (desktop)
const CHART_HEIGHT_VH_MOBILE = 0.70; // chart height on mobile
const CHART_HEIGHT_FALLBACK = 600;
const PAD_L = 120;             // left padding
const PAD_R = 120;             // right padding
const PAD_T = 80;             // top padding
const PAD_B = 70;             // bottom padding
const LABEL_DIM_OPACITY = 0;  // opacity of past labels (0-1)
const BEZIER_TENSION = 0.35;  // curve smoothness (0=straight, 0.5=very curvy)
const MAX_T = 11;             // max time step
const PATH_LABEL_FONT = 11;   // font size for text following the path
// ============ END CONFIG ============

const FIXED_W_STATIC = CHART_FIXED_W;
const makeToX = (w: number) => (t: number) => PAD_L + (t / MAX_T) * (w - PAD_L - PAD_R);
const makeToY = (h: number) => (v: number) => PAD_T + (h - PAD_T - PAD_B) - (v / 100) * (h - PAD_T - PAD_B);
const toXStatic = makeToX(FIXED_W_STATIC);

// Build full smooth cubic bezier path from all milestones
const buildFullPath = (milestones: Milestone[], toX: (t: number) => number, toY: (v: number) => number) => {
  if (milestones.length < 2) return '';
  const points = milestones.map(m => ({ x: toX(m.t), y: toY(m.value) }));
  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const tension = (curr.x - prev.x) * BEZIER_TENSION;
    d += ` C${prev.x + tension},${prev.y} ${curr.x - tension},${curr.y} ${curr.x},${curr.y}`;
  }
  return d;
};

// Use a hidden SVG path to measure actual bezier lengths
const calcCumulativeLengths = (milestones: Milestone[], toX: (t: number) => number, toY: (v: number) => number) => {
  const lengths = [0];

  // Build each segment as its own path and measure
  for (let i = 1; i < milestones.length; i++) {
    const prev = { x: toX(milestones[i - 1].t), y: toY(milestones[i - 1].value) };
    const curr = { x: toX(milestones[i].t), y: toY(milestones[i].value) };
    const tension = (curr.x - prev.x) * BEZIER_TENSION;
    const d = `M${prev.x},${prev.y} C${prev.x + tension},${prev.y} ${curr.x - tension},${curr.y} ${curr.x},${curr.y}`;

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', d);
    const segLen = path.getTotalLength();
    lengths.push(lengths[i - 1] + segLen);
  }

  return { lengths, total: lengths[lengths.length - 1] };
};

type RacerConfig = {
  name: string;
  milestones: Milestone[];
  color: string;
  lastColor?: string;
  defaultSide: 'above' | 'below';
  defaultPathLabelAlign?: 'start' | 'middle' | 'end';
};

const racers: RacerConfig[] = [
  { name: 'Vibe Coding', milestones: vibeMilestones, color: '#9394fa', lastColor: '#ff5252', defaultSide: 'above', defaultPathLabelAlign: 'middle' },
  { name: 'Hiring Devs', milestones: inhouseMilestones, color: '#999', defaultSide: 'below', defaultPathLabelAlign: 'end' },
  { name: 'Value Coding', milestones: cvtMilestones, color: '#9949c8', lastColor: '#4caf50', defaultSide: 'below', defaultPathLabelAlign: 'start' },
];

// Custom smooth scroll with controllable duration
const smoothScrollTo = (el: HTMLElement, target: number, duration: number) => {
  const start = el.scrollLeft;
  const distance = target - start;
  const startTime = performance.now();
  const animate = (now: number) => {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
    el.scrollLeft = start + distance * eased;
    if (progress < 1) requestAnimationFrame(animate);
  };
  requestAnimationFrame(animate);
};

export type SpeedMode = 'slow' | 'paced' | 'fast';

export type RaceAnimationHandle = {
  skip: () => void;
  restart: () => void;
  pause: () => void;
  play: () => void;
  goToStep: (step: number) => void;
  setSpeed: (speed: SpeedMode) => void;
  setHighlight: (racerIndices: number[] | null) => void;
  animationDone: boolean;
  isPaused: boolean;
  currentStep: number;
  maxStep: number;
  speed: SpeedMode;
  highlight: number[] | null;
};

const SPEED_INTERVALS: Record<SpeedMode, number> = {
  slow: 5000,
  paced: 4000,
  fast: 1500,
};

export const RaceAnimation = React.forwardRef<RaceAnimationHandle>((_, fwdRef) => {
  const { isMobile } = useContext(LayoutContext);
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Per-racer animation state — start at step 0 (first dots visible immediately)
  const [racerVisible, setRacerVisible] = useState<number[]>(racers.map(() => 0));
  const [racerDrawn, setRacerDrawn] = useState<number[]>(racers.map(() => 0));
  const [keepAtItVisible, setKeepAtItVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const [speed, setSpeed] = useState<SpeedMode>('paced');
  const [highlight, setHighlight] = useState<number[] | null>(null); // null = show all

  // Sequencer: global tick drives per-step, per-racer turns
  // tick 0: vibe step 0, tick 1: inhouse step 0, tick 2: cvt step 0
  // tick 3: vibe step 1, tick 4: inhouse step 1, tick 5: cvt step 1, etc.
  // Start at racers.length since step 0 is pre-shown for all racers
  const [globalTick, setGlobalTick] = useState(racers.length - 1);

  const racerMaxSteps = racers.map(r => Math.max(...r.milestones.map(m => m.t)));
  const totalTicks = (Math.max(...racerMaxSteps) + 1) * racers.length;

  const allRacersDone = racers.every((_, i) => racerVisible[i] >= racerMaxSteps[i]);
  const animationDone = allRacersDone && keepAtItVisible;
  const started = true; // step 0 is always pre-shown

  // Screen Wake Lock: prevent screen dimming while animation plays
  const wakeLockRef = useRef<any>(null);
  useEffect(() => {
    if (inView && !animationDone && !wakeLockRef.current) {
      (navigator as any).wakeLock?.request('screen')
        .then((lock: any) => { wakeLockRef.current = lock; })
        .catch(() => {}); // silently fail if not supported
    }
    if (animationDone && wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, [inView, animationDone]);

  // Which racer is currently drawing (active)
  const activeRacer = globalTick < totalTicks ? globalTick % racers.length : -1;

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !inView) setInView(true);
    }, { threshold: 0.15 });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line
  }, [inView]);

  // Show keepAtIt when vibe coding (racer 0) reaches its last step
  useEffect(() => {
    if (racerDrawn[0] >= racerMaxSteps[0] && !keepAtItVisible) {
      setKeepAtItVisible(true);
    }
  }, [racerDrawn]); // eslint-disable-line

  // Tick sequencer: each tick advances one racer by one step
  useEffect(() => {
    if (!inView || paused || globalTick >= totalTicks) return;
    const timer = setTimeout(() => setGlobalTick(t => t + 1), SPEED_INTERVALS[speed]);
    return () => clearTimeout(timer);
  }, [inView, globalTick, paused, speed]); // eslint-disable-line

  // Map global tick to per-racer step advancement
  // If the racer is already at max, skip its turn immediately
  useEffect(() => {
    if (globalTick < 0 || globalTick >= totalTicks) return;
    const step = Math.floor(globalTick / racers.length);
    const ri = globalTick % racers.length;
    const maxStep = racerMaxSteps[ri];
    const targetStep = Math.min(step, maxStep);

    // If this racer is already at or beyond the target, skip its turn
    if (racerVisible[ri] >= targetStep) {
      // Auto-advance to next tick without delay
      setGlobalTick(t => t + 1);
      return;
    }

    setRacerVisible(prev => {
      const next = [...prev];
      next[ri] = targetStep;
      return next;
    });
  }, [globalTick]); // eslint-disable-line

  // Mark racer step as drawn after line transition
  useEffect(() => {
    const timers = racers.map((_, ri) => {
      if (racerVisible[ri] >= 0 && racerVisible[ri] > racerDrawn[ri]) {
        return setTimeout(() => {
          setRacerDrawn(prev => {
            const next = [...prev];
            next[ri] = racerVisible[ri];
            return next;
          });
        }, LINE_DRAW_MS);
      }
      return null;
    });
    return () => timers.forEach(t => t && clearTimeout(t));
  }, [racerVisible]); // eslint-disable-line

  // Auto-scroll to follow the current step
  useEffect(() => {
    if (!scrollRef.current) return;
    const step = Math.floor(globalTick / racers.length);
    const targetX = toXStatic(step);
    const container = scrollRef.current;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const scrollTarget = Math.min(
      Math.max(0, targetX - container.clientWidth / 2),
      maxScroll - container.clientWidth / 4 // don't scroll fully to the end, keep last steps centered
    );
    smoothScrollTo(container, scrollTarget, SCROLL_DURATION);
  }, [globalTick]); // eslint-disable-line

  // Mobile: simulate hover on the most visible column when scrolling
  useEffect(() => {
    if (!isMobile || !animationDone || !scrollRef.current) return;
    const container = scrollRef.current;
    const handleScroll = () => {
      const centerX = container.scrollLeft + container.clientWidth / 2;
      // Find which step's X is closest to the center of the viewport
      let closestStep = 0;
      let closestDist = Infinity;
      for (let t = 0; t <= MAX_T; t++) {
        const x = toXStatic(t);
        const dist = Math.abs(x - centerX);
        if (dist < closestDist) {
          closestDist = dist;
          closestStep = t;
        }
      }
      setHoveredStep(closestStep);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // initial
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isMobile, animationDone]); // eslint-disable-line

  // Current logical step (0-based, shared across racers)
  const currentStep = Math.floor(Math.max(0, globalTick) / racers.length);
  const maxStepValue = Math.max(...racerMaxSteps);

  const skipAnimation = () => {
    setRacerVisible(racers.map((_, i) => racerMaxSteps[i]));
    setRacerDrawn(racers.map((_, i) => racerMaxSteps[i]));
    setGlobalTick(totalTicks);
    setKeepAtItVisible(true);
    setTimeout(() => {
      if (scrollRef.current) {
        smoothScrollTo(scrollRef.current, scrollRef.current.scrollWidth, SCROLL_DURATION);
      }
    }, 100);
  };

  const restartAnimation = () => {
    setPaused(false);
    setRacerVisible(racers.map(() => 0));
    setRacerDrawn(racers.map(() => 0));
    setGlobalTick(racers.length - 1);
    setKeepAtItVisible(false);
    if (scrollRef.current) {
      smoothScrollTo(scrollRef.current, 0, SCROLL_DURATION);
    }
  };

  const goToStep = (step: number) => {
    const targetTick = (step + 1) * racers.length - 1;
    // Set all racers to their state at this step
    setRacerVisible(racers.map((_, i) => Math.min(step, racerMaxSteps[i])));
    setRacerDrawn(racers.map((_, i) => Math.min(step, racerMaxSteps[i])));
    setGlobalTick(targetTick);
    setKeepAtItVisible(step >= racerMaxSteps[0]); // show keepAtIt if vibe is done
    if (scrollRef.current) {
      const targetX = toXStatic(step);
      const scrollTarget = Math.max(0, targetX - scrollRef.current.clientWidth / 2);
      smoothScrollTo(scrollRef.current, scrollTarget, SCROLL_DURATION);
    }
  };

  useImperativeHandle(fwdRef, () => ({
    skip: skipAnimation,
    restart: restartAnimation,
    pause: () => setPaused(true),
    play: () => setPaused(false),
    goToStep,
    setSpeed,
    setHighlight,
    animationDone,
    isPaused: paused,
    currentStep,
    maxStep: maxStepValue,
    speed,
    highlight,
  }), [animationDone, currentStep, paused, speed, highlight]); // eslint-disable-line

  const FIXED_W = CHART_FIXED_W;
  const chartHeight = typeof window !== 'undefined' ? Math.round(window.innerHeight * (isMobile ? CHART_HEIGHT_VH_MOBILE : CHART_HEIGHT_VH)) : CHART_HEIGHT_FALLBACK;

  const toX = makeToX(FIXED_W);
  const toY = makeToY(chartHeight);
  const CHART_W = FIXED_W - PAD_L - PAD_R;
  const CHART_H = chartHeight - PAD_T - PAD_B;
  const fs = isMobile ? 13 : 16;
  const emojiSize = isMobile ? 26 : 38;
  const strokeBlur = isMobile ? 8 : 12;

  return (
    <Box ref={containerRef} sx={{ width: '100%', mt: -6, position: 'relative' }}>

      {started && (
        <Box sx={{ position: 'relative' }}>
        {/* Sticky axis labels — desktop only */}
        {!isMobile && (
          <>
            <Typography sx={{
              position: 'absolute', left: 100, top: toY(103) - 6,
              color: 'rgba(255,255,255,0.5)', fontSize: 13,
              fontFamily: "'Montserrat', sans-serif", fontWeight: 500,
              zIndex: 3, pointerEvents: 'none',
            }}>
              Outcome - (Profitability / Product Market Fit)
            </Typography>
            <Typography sx={{
              position: 'absolute', left: 100, bottom: PAD_B - 30,
              color: 'rgba(255,255,255,0.5)', fontSize: 13,
              fontFamily: "'Montserrat', sans-serif", fontWeight: 500,
              zIndex: 3, pointerEvents: 'none',
            }}>
              Time →
            </Typography>
          </>
        )}
        <Box
          ref={scrollRef}
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            width: '100%',
            scrollbarWidth: 'none' as any,
            msOverflowStyle: 'none' as any,
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
        <svg
          viewBox={`0 0 ${FIXED_W} ${chartHeight}`}
          width={FIXED_W}
          height={chartHeight}
          style={{ overflow: 'visible', display: 'block', pointerEvents: animationDone ? 'auto' : 'none' }}
        >

          {/* Axes lines — desktop only */}
          {!isMobile && (
            <g>
              <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + CHART_H} stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
              <line x1={PAD_L} y1={PAD_T + CHART_H} x2={PAD_L + CHART_W} y2={PAD_T + CHART_H} stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
              <line x1={PAD_L} y1={toY(100)} x2={PAD_L + CHART_W} y2={toY(100)} stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="8 6"/>
            </g>
          )}

          {/* Pass 1: Lines only (lower z-order) */}
          {racers.map((racer, ri) => {
            const rv = racerVisible[ri];
            const rd = racerDrawn[ri];
            const isActive = activeRacer === ri;
            const isInactive = activeRacer >= 0 && !isActive;
            const isHighlighted = highlight === null || highlight.includes(ri);
            const fullPath = buildFullPath(racer.milestones, toX, toY);
            const { lengths, total } = calcCumulativeLengths(racer.milestones, toX, toY);
            const lastVisibleIdx = racer.milestones.filter(m => m.t <= rv).length - 1;
            const revealedLength = lastVisibleIdx >= 0 ? lengths[lastVisibleIdx] : 0;
            const hiddenLength = total - revealedLength;

            return (
              <g key={ri} style={{ opacity: !isHighlighted ? 0.1 : isInactive ? 0.25 : 1, transition: 'opacity 0.4s ease' }}>
                {fullPath && (
                  <>
                  <path
                    d={fullPath}
                    fill="none"
                    stroke={racer.color}
                    strokeWidth={isMobile ? 2 : 3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray={total}
                    strokeDashoffset={hiddenLength}
                    style={{
                      transition: `stroke-dashoffset ${LINE_DRAW_MS}ms ease-in-out`,
                    }}
                  />
                  {/* Per-segment curved line labels */}
                  {racer.milestones.slice(0, -1).map((m, si) => {
                    const next = racer.milestones[si + 1];
                    if (!next || next.t > rd) return null;
                    if (m.hidePathLabel) return null;

                    // Build a single-segment bezier for this pair
                    const p0 = { x: toX(m.t), y: toY(m.value) };
                    const p1 = { x: toX(next.t), y: toY(next.value) };
                    const tension = (p1.x - p0.x) * BEZIER_TENSION;
                    const plPos = m.pathLabelPos ?? (racer.defaultSide === 'above' ? 'below' : 'above');
                    // Increase offset on steep segments so label doesn't overlap the line
                    const slope = Math.abs(p1.y - p0.y) / Math.max(1, p1.x - p0.x);
                    const steepExtra = Math.min(8, slope * 6);
                    const offsetY = plPos === 'above' ? -(10 + steepExtra) : (14 + steepExtra);
                    const segD = `M${p0.x},${p0.y + offsetY} C${p0.x + tension},${p0.y + offsetY} ${p1.x - tension},${p1.y + offsetY} ${p1.x},${p1.y + offsetY}`;
                    const segId = `seg-path-${ri}-${si}`;

                    const align = m.pathLabelAlign ?? racer.defaultPathLabelAlign ?? 'middle';
                    const startOffset = align === 'start' ? '10%' : align === 'end' ? '90%' : '50%';
                    const textAnchor = align === 'start' ? 'start' : align === 'end' ? 'end' : 'middle';

                    const segIsCurrent = next.t === rd;
                    const segIsPast = next.t < rd;
                    const segLeadsToHover = hoveredStep !== null && next.t <= hoveredStep;
                    const labelOpacity = segIsCurrent ? 0.8 : (segIsPast && segLeadsToHover) ? 0.6 : 0;

                    return (
                      <g key={`seg-${ri}-${si}`}>
                        <defs>
                          <path id={segId} d={segD} />
                        </defs>
                        <text
                          fill="white"
                          fontSize={PATH_LABEL_FONT}
                          fontFamily="'Montserrat', sans-serif"
                          fontWeight={600}
                          opacity={labelOpacity}
                          style={{ transition: 'opacity 0.3s ease' }}
                        >
                          <textPath href={`#${segId}`} startOffset={startOffset} textAnchor={textAnchor}>
                            {racer.name}
                          </textPath>
                        </text>
                      </g>
                    );
                  })}
                  </>
                )}

              </g>
            );
          })}

          {/* Dashed "keep at it" continuation for vibe coding */}
          {keepAtItVisible && (() => {
            const keepPath = buildFullPath(vibeKeepAtIt, toX, toY);
            const { lengths: keepLengths, total: keepTotal } = calcCumulativeLengths(vibeKeepAtIt, toX, toY);
            const keepVisible = vibeKeepAtIt.length - 1;
            const keepRevealed = keepVisible >= 0 ? keepLengths[keepVisible] : 0;
            const keepHidden = keepTotal - keepRevealed;
            return keepPath ? (
              <path
                d={keepPath}
                fill="none"
                stroke="#9394fa"
                strokeWidth={isMobile ? 2 : 3}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={`8 6`}
                strokeDashoffset={keepHidden > 0.1 ? keepTotal : 0}
                style={{ transition: 'stroke-dashoffset 1s ease-in-out', opacity: 0.6 }}
              />
            ) : null;
          })()}

          {/* Hover columns — full-height invisible rects for hover detection */}
          {Array.from({ length: MAX_T + 1 }, (_, t) => (
            <rect
              key={`hover-col-${t}`}
              x={toX(t) - (CHART_W / MAX_T) / 2}
              y={PAD_T}
              width={CHART_W / MAX_T}
              height={CHART_H}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoveredStep(t)}
              onMouseLeave={() => setHoveredStep(null)}
            />
          ))}

          {/* Pass 2: Emojis + labels (higher z-order, on top of all lines) */}
          {racers.map((racer, ri) => {
            const rv = racerVisible[ri];
            const rd = racerDrawn[ri];
            const isDrawing = rv > rd; // line is currently extending
            const isActive = activeRacer === ri;
            const isInactive = activeRacer >= 0 && !isActive;
            const isHighlighted = highlight === null || highlight.includes(ri);
            const drawnMilestones = racer.milestones.filter(m => m.t >= 0);
            return (
              <g key={`labels-${ri}`}>
                {drawnMilestones.map((m, mi) => {
                  const cx = toX(m.t);
                  const cy = toY(m.value);
                  const isLast = mi === racer.milestones.length - 1;
                  const pos = m.labelPos ?? racer.defaultSide;
                  const labelY = pos === 'above' ? cy - emojiSize - 5 : cy + emojiSize + 5;
                  const textColor = isLast && racer.lastColor ? racer.lastColor : racer.color;
                  const isStepDrawn = m.t <= rd;
                  if (!isStepDrawn) return null;
                  const isNewest = !animationDone && m.t === rd;
                  const isPast = m.t < rd;
                  const isHovered = hoveredStep === m.t;
                  const isFirst = mi === 0;
                  const isOutcome = m.value === 100 || m.value === 0;
                  // Hide departure label while line is drawing away from it
                  const isDeparture = isDrawing && m.t === rd;
                  const labelDimmed = isPast && !isHovered && !isOutcome && !isFirst;
                  // Inactive/unhighlighted racers: dim emojis, hide labels
                  const emojiOpacity = !isHighlighted ? 0.1 : isInactive ? 0.25 : (isNewest ? 0 : 1);
                  const shouldHideLabel = !isHighlighted || isInactive || isDeparture;

                  return (
                    <g key={mi} style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHoveredStep(m.t)}
                      onMouseLeave={() => setHoveredStep(null)}
                    >
                      {/* Emoji or image */}
                      {m.image ? (
                        <image
                          href={m.image}
                          x={cx - emojiSize / 2} y={cy - emojiSize / 2}
                          width={emojiSize} height={emojiSize}
                          style={{
                            opacity: emojiOpacity,
                            transition: 'opacity 0.3s ease',
                            animation: isNewest && !isInactive ? 'fadeIn 0.5s ease forwards' : 'none',
                          }}
                        />
                      ) : m.emoji ? (
                        <text
                          x={cx} y={cy}
                          textAnchor="middle" dominantBaseline="central"
                          fontSize={emojiSize}
                          style={{
                            opacity: emojiOpacity,
                            transition: 'opacity 0.3s ease',
                            animation: isNewest && !isInactive ? 'fadeIn 0.5s ease forwards' : 'none',
                          }}
                        >
                          {m.emoji}
                        </text>
                      ) : null}

                      {/* Label */}
                      {m.label && (() => {
                        const lines = m.label.split('\n');
                        const lineHeight = fs + 4;
                        const totalTextHeight = lines.length * lineHeight;
                        const textStartY = pos === 'above'
                          ? labelY - totalTextHeight + lineHeight
                          : labelY;

                        const labelOpacity = shouldHideLabel ? 0 : isNewest ? 0 : labelDimmed ? LABEL_DIM_OPACITY : 1;
                        return (
                          <g style={{
                            opacity: labelOpacity,
                            transition: 'opacity 0.3s ease',
                            animation: (isNewest && !isInactive && !shouldHideLabel) ? 'fadeIn 0.5s 0.2s ease forwards' : 'none',
                          }}>
                            {/* Connector */}
                            <line
                              x1={cx} y1={cy + (pos === 'above' ? -emojiSize / 2 - 2 : emojiSize / 2 + 2)}
                              x2={cx} y2={textStartY + (pos === 'above' ? totalTextHeight - lineHeight + 4 : -6)}
                              stroke={textColor} strokeWidth={0.7} opacity={0.3}
                            />
                            {/* Stroke layer */}
                            {lines.map((line, li) => (
                              <text key={`s${li}`} x={cx} y={textStartY + li * lineHeight}
                                fill="none" stroke="black" strokeWidth={strokeBlur} strokeLinejoin="round"
                                fontSize={fs} textAnchor="middle" fontFamily="'Montserrat', sans-serif"
                                fontWeight={isLast ? 700 : 500}>
                                {line}
                              </text>
                            ))}
                            {/* Fill layer */}
                            {lines.map((line, li) => (
                              <text key={`f${li}`} x={cx} y={textStartY + li * lineHeight}
                                fill={textColor} fontSize={fs} textAnchor="middle"
                                fontFamily="'Montserrat', sans-serif" fontWeight={isLast ? 700 : 500} opacity={0.95}>
                                {line}
                              </text>
                            ))}
                          </g>
                        );
                      })()}
                    </g>
                  );
                })}
              </g>
            );
          })}

          {/* "Keep at it" label for vibe coding dashed extension */}
          {keepAtItVisible && (() => {
            const m = vibeKeepAtIt[vibeKeepAtIt.length - 1];
            const cx = toX(m.t);
            const cy = toY(m.value);
            const pos = m.labelPos ?? 'above';
            const labelY = pos === 'above' ? cy - emojiSize - 5 : cy + emojiSize + 5;
            const lines = m.label.split('\n');
            const lineHeight = fs + 4;
            const totalTextHeight = lines.length * lineHeight;
            const textStartY = pos === 'above' ? labelY - totalTextHeight + lineHeight : labelY;

            return (
              <g style={{ animation: 'fadeIn 0.5s ease forwards', opacity: 0 }}>
                {m.emoji && (
                  <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={emojiSize}>
                    {m.emoji}
                  </text>
                )}
                <line
                  x1={cx} y1={cy + (pos === 'above' ? -emojiSize / 2 - 2 : emojiSize / 2 + 2)}
                  x2={cx} y2={textStartY + (pos === 'above' ? totalTextHeight - lineHeight + 4 : -6)}
                  stroke="#9394fa" strokeWidth={0.7} opacity={0.3}
                />
                {lines.map((line, li) => (
                  <text key={`ks${li}`} x={cx} y={textStartY + li * lineHeight}
                    fill="none" stroke="black" strokeWidth={strokeBlur} strokeLinejoin="round"
                    fontSize={fs} textAnchor="middle" fontFamily="'Montserrat', sans-serif" fontWeight={500}>
                    {line}
                  </text>
                ))}
                {lines.map((line, li) => (
                  <text key={`kf${li}`} x={cx} y={textStartY + li * lineHeight}
                    fill="#9394fa" fontSize={fs} textAnchor="middle"
                    fontFamily="'Montserrat', sans-serif" fontWeight={500} opacity={0.95}>
                    {line}
                  </text>
                ))}
              </g>
            );
          })()}
        </svg>
        </Box>
        </Box>
      )}

      {!started && <Box sx={{ height: chartHeight }} />}

      {/* Fade-in keyframe */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
});
